import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, Mail, UserPlus, ArrowLeft, Check } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Access request state
  const [showRequest, setShowRequest] = useState(false);
  const [reqForm, setReqForm] = useState({ first_name: "", last_name: "", login: "", password: "", password2: "" });
  const [reqError, setReqError] = useState("");
  const [reqSuccess, setReqSuccess] = useState(false);
  const [reqLoading, setReqLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    setReqError("");

    if (!reqForm.first_name || !reqForm.last_name || !reqForm.login) {
      setReqError("Tous les champs sont obligatoires");
      return;
    }
    if (reqForm.password.length < 6) {
      setReqError("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    if (reqForm.password !== reqForm.password2) {
      setReqError("Les mots de passe ne correspondent pas");
      return;
    }

    setReqLoading(true);
    try {
      await axios.post(`${API}/api/auth/request-access`, {
        first_name: reqForm.first_name,
        last_name: reqForm.last_name,
        login: reqForm.login,
        password: reqForm.password,
      });
      setReqSuccess(true);
    } catch (err) {
      setReqError(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setReqLoading(false);
    }
  };

  // Success screen after request
  if (reqSuccess) {
    return (
      <div className="crm-login-bg">
        <div className="crm-login-card">
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="text-green-600" size={28} />
            </div>
          </div>
          <h2 className="text-xl font-bold text-[#162d3d] text-center mb-2">Demande envoyée !</h2>
          <p className="text-sm text-[#577083] text-center mb-6">
            Votre demande d'accès a été transmise à l'administrateur.<br />
            Vous recevrez une confirmation une fois votre compte validé.
          </p>
          <Button
            onClick={() => { setShowRequest(false); setReqSuccess(false); setReqForm({ first_name: "", last_name: "", login: "", password: "", password2: "" }); }}
            className="w-full h-11 bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d] font-semibold"
            data-testid="back-to-login-btn"
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour à la connexion
          </Button>
        </div>
      </div>
    );
  }

  // Access request form
  if (showRequest) {
    return (
      <div className="crm-login-bg">
        <div className="crm-login-card">
          <div className="flex justify-center mb-6">
            <img
              src="https://customer-assets.emergentagent.com/job_wix-form-replica/artifacts/8c7fc1k3_logofinal-logo-full-color-rgb-900px-w-72ppi.png"
              alt="B.health"
              className="h-16 object-contain"
            />
          </div>
          <h2 className="text-xl font-bold text-[#162d3d] text-center mb-1">Demande d'accès</h2>
          <p className="text-sm text-[#577083] text-center mb-6">Remplissez le formulaire ci-dessous. L'administrateur validera votre accès.</p>
          <form onSubmit={handleRequestAccess} className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-3">
              <Input
                placeholder="Prénom *" value={reqForm.first_name}
                onChange={(e) => setReqForm({...reqForm, first_name: e.target.value})}
                className="h-11 border-[#c1c9d0] focus:border-[#3899ec]"
                data-testid="req-first-name"
                required
              />
              <Input
                placeholder="Nom *" value={reqForm.last_name}
                onChange={(e) => setReqForm({...reqForm, last_name: e.target.value})}
                className="h-11 border-[#c1c9d0] focus:border-[#3899ec]"
                data-testid="req-last-name"
                required
              />
            </div>
            <Input
              placeholder="Identifiant souhaité *" value={reqForm.login}
              onChange={(e) => setReqForm({...reqForm, login: e.target.value})}
              className="h-11 border-[#c1c9d0] focus:border-[#3899ec]"
              data-testid="req-login"
              required
            />
            <Input
              type="password" placeholder="Mot de passe (min. 6 caractères) *"
              value={reqForm.password}
              onChange={(e) => setReqForm({...reqForm, password: e.target.value})}
              className="h-11 border-[#c1c9d0] focus:border-[#3899ec]"
              data-testid="req-password"
              required
            />
            <Input
              type="password" placeholder="Confirmer le mot de passe *"
              value={reqForm.password2}
              onChange={(e) => setReqForm({...reqForm, password2: e.target.value})}
              className="h-11 border-[#c1c9d0] focus:border-[#3899ec]"
              data-testid="req-password2"
              required
            />
            {reqError && <p className="text-sm text-red-500 text-center">{reqError}</p>}
            <Button type="submit" disabled={reqLoading} className="h-11 bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d] font-semibold" data-testid="submit-request-btn">
              {reqLoading ? "Envoi..." : "Envoyer la demande"}
            </Button>
            <button
              type="button"
              onClick={() => { setShowRequest(false); setReqError(""); }}
              className="text-sm text-[#3899ec] hover:underline text-center mt-1"
              data-testid="back-to-login-link"
            >
              <ArrowLeft size={14} className="inline mr-1" />
              Retour à la connexion
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Default login form
  return (
    <div className="crm-login-bg">
      <div className="crm-login-card">
        <div className="flex justify-center mb-6">
          <img
            src="https://customer-assets.emergentagent.com/job_wix-form-replica/artifacts/8c7fc1k3_logofinal-logo-full-color-rgb-900px-w-72ppi.png"
            alt="B.health"
            className="h-16 object-contain"
          />
        </div>
        <h2 className="text-xl font-bold text-[#162d3d] text-center mb-1">CRM B.Health</h2>
        <p className="text-sm text-[#577083] text-center mb-6">Connectez vous pour accéder au formulaire d'inscription</p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a1aeba]" size={18} />
            <Input
              type="text" placeholder="Identifiant" value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10 h-11 border-[#c1c9d0] focus:border-[#3899ec]"
              data-testid="login-email"
              required
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a1aeba]" size={18} />
            <Input
              type="password" placeholder="Mot de passe" value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10 h-11 border-[#c1c9d0] focus:border-[#3899ec]"
              data-testid="login-password"
              required
            />
          </div>
          {error && <p className="text-sm text-red-500 text-center" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading} className="h-11 bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d] font-semibold" data-testid="login-submit-btn">
            {loading ? "Connexion..." : "Se connecter"}
          </Button>
        </form>
        <div className="mt-5 pt-4 border-t border-[#edf0f4] text-center">
          <button
            onClick={() => setShowRequest(true)}
            className="inline-flex items-center gap-2 text-sm text-[#577083] hover:text-[#3899ec] transition-colors"
            data-testid="request-access-btn"
          >
            <UserPlus size={16} />
            Demander l'accès
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
